import { calculateScore } from "@solitaire/game-engine";
import type { GameState, ScoreConfig, DrawMode } from "@solitaire/game-engine";
import { supabase } from "./supabase.js";

// ============================================================
// Types
// ============================================================

export interface LeaderboardEntry {
  rank: number;
  userId: string;
  username: string;
  score: number;
  elapsedSeconds: number;
  moves: number;
  shakeCount: number;
  undoCount: number;
  achievedAt: string;
  won: boolean;
}

// ============================================================
// saveScore
// Speichert nur wenn kein Eintrag existiert ODER neuer Score besser ist.
// Gibt den aktuellen Rang zurück (1-basiert) oder null bei Fehler.
// ============================================================

export async function saveScore(
  gameState: GameState,
  userId: string,
  config: ScoreConfig,
  won = true,
  drawMode: DrawMode = 1
): Promise<{ rank: number | null; error: string | null }> {
  const newScore = calculateScore(gameState, config, won);

  // Prüfen ob bereits ein Eintrag für diesen User+Seed+DrawMode existiert
  const { data: existing } = await supabase
    .from("scores")
    .select("id, score")
    .eq("user_id", userId)
    .eq("seed", gameState.seed)
    .eq("draw_mode", drawMode)
    .maybeSingle();

  const payload = {
    user_id:         userId,
    seed:            gameState.seed,
    draw_mode:       drawMode,
    score:           newScore,
    elapsed_seconds: gameState.elapsedSeconds,
    moves:           gameState.moves,
    shake_count:     gameState.shakeCount,
    undo_count:      gameState.undoCount,
    won,
    achieved_at:     new Date().toISOString(),
  };

  let dbError: string | null = null;

  if (!existing) {
    // Kein vorheriger Eintrag → insert
    const { error } = await supabase.from("scores").insert(payload);
    if (error) dbError = error.message;
  } else if (newScore > (existing.score as number)) {
    // Vorheriger Eintrag vorhanden, neuer Score ist besser → update
    const { error } = await supabase
      .from("scores")
      .update(payload)
      .eq("id", existing.id);
    if (error) dbError = error.message;
  }
  // Andernfalls: neuer Score schlechter → nichts tun

  if (dbError) {
    console.error("[scores] saveScore failed:", dbError);
    return { rank: null, error: dbError };
  }

  // Rang ermitteln
  const entries = await fetchLeaderboard(gameState.seed, drawMode);
  const idx = entries.findIndex((e) => e.userId === userId);
  const rank = idx >= 0 ? idx + 1 : null;

  return { rank, error: null };
}

// ============================================================
// fetchLeaderboard — Top 100 für einen Seed, sortiert by score DESC
// ============================================================

export async function fetchLeaderboard(seed: number, drawMode: DrawMode = 1): Promise<LeaderboardEntry[]> {
  // Timeout: nach 8s abbrechen (verhindert endloses "Lade…" nach Inaktivität)
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);

  try {
    const { data, error } = await supabase
      .from("scores")
      .select("user_id, score, elapsed_seconds, moves, shake_count, undo_count, achieved_at, won, profiles(username)")
      .eq("seed", seed)
      .eq("draw_mode", drawMode)
      .order("score", { ascending: false })
      .limit(100)
      .abortSignal(controller.signal);

    clearTimeout(timer);

    if (error || !data) {
      console.error("[scores] fetchLeaderboard failed:", error?.message);
      throw new Error(error?.message ?? "No data");
    }


    return data.map((row, idx) => {
      const profile = Array.isArray(row.profiles) ? row.profiles[0] : row.profiles;
      return {
        rank:           idx + 1,
        userId:         row.user_id as string,
        username:       (profile as { username: string } | null)?.username ?? "?",
        score:          row.score as number,
        elapsedSeconds: row.elapsed_seconds as number,
        moves:          row.moves as number,
        shakeCount:     row.shake_count as number,
        undoCount:      row.undo_count as number,
        achievedAt:     row.achieved_at as string,
        won:            (row.won as boolean) ?? false,
      };
    });
  } catch (err) {
    clearTimeout(timer);
    console.error("[scores] fetchLeaderboard failed:", err);
    throw err;
  }
}

// ============================================================
// fetchOwnRank — Eigene Platzierung für einen Seed
// ============================================================

export interface OwnRankInfo {
  rank: number;
  total: number;
  entry: LeaderboardEntry;
}

export async function fetchOwnRank(seed: number, userId: string, drawMode: DrawMode = 1): Promise<OwnRankInfo | null> {
  const { data: own } = await supabase
    .from("scores")
    .select("user_id, score, elapsed_seconds, moves, shake_count, undo_count, achieved_at, won, profiles(username)")
    .eq("seed", seed)
    .eq("draw_mode", drawMode)
    .eq("user_id", userId)
    .maybeSingle();

  if (!own) return null;

  const userScore = own.score as number;

  // Parallel: Anzahl bessere Scores + Gesamtzahl
  const [{ count: ahead }, { count: total }] = await Promise.all([
    supabase
      .from("scores")
      .select("*", { count: "exact", head: true })
      .eq("seed", seed)
      .eq("draw_mode", drawMode)
      .gt("score", userScore),
    supabase
      .from("scores")
      .select("*", { count: "exact", head: true })
      .eq("seed", seed)
      .eq("draw_mode", drawMode),
  ]);

  const rank = (ahead ?? 0) + 1;
  const profile = Array.isArray(own.profiles) ? own.profiles[0] : own.profiles;

  return {
    rank,
    total: total ?? 0,
    entry: {
      rank,
      userId: own.user_id as string,
      username: (profile as { username: string } | null)?.username ?? "?",
      score: userScore,
      elapsedSeconds: own.elapsed_seconds as number,
      moves: own.moves as number,
      shakeCount: own.shake_count as number,
      undoCount: own.undo_count as number,
      achievedAt: own.achieved_at as string,
      won: (own.won as boolean) ?? false,
    },
  };
}

// ============================================================
// fetchDailyStats — Kalender-Daten für einen Monat (won/lost pro Tag)
// ============================================================

export interface DailyStat {
  day: number;      // 1–31
  won: boolean;
}

export async function fetchDailyStats(
  userId: string,
  year: number,
  month: number,   // 1–12
  drawMode: DrawMode = 1
): Promise<DailyStat[]> {
  // Daily seeds: YYYYMMDD → z.B. 20260301 … 20260331
  const startSeed = year * 10000 + month * 100 + 1;
  const endSeed   = year * 10000 + month * 100 + 31;

  const { data, error } = await supabase
    .from("scores")
    .select("seed, won")
    .eq("user_id", userId)
    .eq("draw_mode", drawMode)
    .gte("seed", startSeed)
    .lte("seed", endSeed);

  if (error || !data) {
    console.error("[scores] fetchDailyStats failed:", error?.message);
    return [];
  }

  return data.map((row) => ({
    day: (row.seed as number) % 100,
    won: row.won as boolean,
  }));
}
